import { Link, useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { SOCIAL_MEDIA } from '@/lib/constants'
import { navGroups, pricingLink } from '@/data/navigation'
import { LinkedInIcon, InstagramIcon, FacebookIcon } from './icons/social-icons'

interface Props {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

export function MobileNav({ isOpen, setIsOpen }: Props) {
  const { pathname } = useLocation()

  const linkClasses = (href: string) =>
    cn(
      'block py-1.5 transition hover:text-[oklch(50%_0.15_275)]',
      pathname === href && 'font-semibold text-[oklch(50%_0.15_275)]'
    )

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      // When closed, mark inert so the off-screen menu is removed from both the
      // accessibility tree and the tab order (its links would otherwise stay
      // keyboard-focusable while invisible).
      {...(!isOpen ? { inert: '' } : {})}
      className={cn(
        'fixed inset-0 z-100 flex h-screen w-screen flex-col bg-white text-gray-900 transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
      )}
    >
      <div className="flex h-full flex-col px-6 py-8">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between">
          <span className="flex items-center gap-2">
            <img src="/logo-mark.svg" alt="" aria-hidden="true" className="h-7 w-auto" />
            <span className="text-lg font-semibold tracking-tight">Fugue Lab</span>
          </span>
          <button onClick={() => setIsOpen(false)} aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Grouped links (scrolls when taller than the viewport) */}
        <nav aria-label="Mobile" className="mt-8 flex-1 overflow-y-auto pb-8">
          <div className="mx-auto grid max-w-md gap-8">
            {navGroups.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">{group.label}</p>
                <div className="mt-2 space-y-0.5 text-lg">
                  {group.items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={linkClasses(item.href)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {group.footer && (
                    <Link
                      to={group.footer.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(linkClasses(group.footer.href), 'text-gray-500')}
                    >
                      {group.footer.label}
                    </Link>
                  )}
                </div>
              </div>
            ))}

            <div className="space-y-3">
              <Link
                to={pricingLink.href}
                onClick={() => setIsOpen(false)}
                className={cn('block text-lg font-medium', linkClasses(pricingLink.href))}
              >
                {pricingLink.label}
              </Link>
              <Link
                to="/get-started"
                onClick={() => setIsOpen(false)}
                className="inline-block rounded bg-black px-6 py-3 text-lg text-white transition hover:bg-gray-800"
              >
                Get Started
              </Link>
            </div>

            <div className="flex gap-4">
              <a
                href={SOCIAL_MEDIA.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <LinkedInIcon className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_MEDIA.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <InstagramIcon className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_MEDIA.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <FacebookIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </nav>

        {/* Footer */}
        <div className="text-muted-foreground flex-shrink-0 border-t border-gray-200 pt-4 text-center text-sm">
          &copy; {new Date().getFullYear()} Fugue Lab
        </div>
      </div>
    </div>
  )
}
